import { CSSProperties, KeyboardEvent, ReactNode } from "react";

import { Badge } from "react-bootstrap";
import styled from "styled-components";

import TooltipProvider from "./TooltipProvider";

// Kept mounted (not display: none) so the chip's width is stable on hover.
const HoverFilterIcon = styled.i`
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s ease-in-out;
`;

// Bootstrap badges default to bold; chip content reads as data, not a label.
// inline-flex lets the content shrink and truncate while the hover icon
// keeps its width.
const ChipBadge = styled(Badge)`
  font-weight: 400;
  font-size: 0.875rem;
  max-width: 100%;
  display: inline-flex;
  align-items: baseline;
  background-color: var(--aa-surface-bg) !important;
  color: var(--aa-text-primary);

  &:hover ${HoverFilterIcon} {
    opacity: 1;
  }
`;

const ChipContent = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export type FilterBadgeProps = {
  content: ReactNode;
  onClick?: () => void;
  tooltip?: ReactNode;
  bg?: string;
  className?: string;
  style?: CSSProperties;
  "data-testid"?: string;
};

/**
 * A compact chip for rendering a value that can be clicked to apply it as a
 * filter (e.g. metadata key/value pairs in a table cell). Omitting onClick
 * renders a non-interactive chip. Interactive chips reveal a filter icon
 * after the content on hover, and tooltip content is shown via
 * TooltipProvider.
 *
 * The chip never grows past its container; overflowing content is truncated
 * with an ellipsis. Pass a max-width via style to cap it tighter.
 */
const FilterBadge = ({
  content,
  onClick,
  tooltip,
  bg = "secondary",
  className,
  style,
  "data-testid": dataTestId,
}: FilterBadgeProps) => {
  return (
    <TooltipProvider tooltip={tooltip}>
      <ChipBadge
        bg={bg}
        className={className}
        style={style}
        data-testid={dataTestId}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick}
        onKeyDown={
          onClick
            ? (e: KeyboardEvent<HTMLElement>) => {
                if (e.key === "Enter" || e.key === " ") {
                  // Keep Space from scrolling the page.
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
      >
        <ChipContent>{content}</ChipContent>
        {onClick && <HoverFilterIcon className="bi-filter ms-1" aria-hidden />}
      </ChipBadge>
    </TooltipProvider>
  );
};

export default FilterBadge;
